import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { makeStyles } from '@material-ui/core/styles';
import { View } from 'components/layout/View';
import MuiAlert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import en from 'config/locales/en';
import { standardizeString } from 'util/general';
import { useToast } from 'components/contexts/ToastContext';
import { useLanguage } from 'components/contexts/LanguageContext';

let openSnackbarFn = () => {};

export default function Toast2(props) {
  const { portal: propPortal, position: propPosition, size: propSize, maxWidth: propMaxWidth, ...otherProps } = props;
  
  let {
    config: { active, variant, duration, text, id, portal, position, size, maxWidth },
    methods: { deactivate },
  } = useToast();
  
  // Override config with props if provided
  const finalPortal = propPortal !== undefined ? propPortal : portal;
  const finalPosition = propPosition || position;
  const finalSize = propSize || size;
  const finalMaxWidth = propMaxWidth || maxWidth;

  const { lang } = useLanguage();

  if (id) {
    text = lang?.[id] ?? standardizeString(id);
  }

  const classes = useStyles({ position: finalPosition, size: finalSize, maxWidth: finalMaxWidth });

  const [closeTimeout, setCloseTimeout] = useState();

  useEffect(() => {
    if (!active) return;
    setCloseTimeout(setTimeout(() => close(), duration));
  }, [active]);

  function close() {
    deactivate();
    clearTimeout(closeTimeout);
  }

  const ToastContent = () => (
    <MuiAlert
      severity={variant}
      variant="filled"
      classes={{
        root: classes.root,
        filledInfo: classes.filledInfo,
        filledSuccess: classes.filledSuccess,
        filledWarning: classes.filledWarning,
        filledError: classes.filledError,
      }}
      style={{ width: finalSize === 'small' ? 'auto' : '100%' }}
      {...otherProps}>
      {text}
    </MuiAlert>
  );

  if (!active) return null;

  // Portal rendering for positioned toasts
  if (finalPortal && finalPosition !== 'inline') {
    return ReactDOM.createPortal(
      <div className={classes.portalContainer}>
        <ToastContent />
      </div>,
      document.body
    );
  }

  // Inline rendering (default behavior)
  return (
    <View ph={0.5} mt={0.5} w={'100%'}>
      <ToastContent />
    </View>
  );
}

const useStyles = makeStyles({
  root: ({ size, maxWidth }) => ({
    wordBreak: 'break-all',
    paddingTop: size === 'small' ? 4 : 0,
    paddingBottom: size === 'small' ? 4 : 0,
    paddingLeft: size === 'small' ? 8 : undefined,
    paddingRight: size === 'small' ? 8 : undefined,
    fontSize: size === 'small' ? '0.75rem' : size === 'large' ? '1rem' : '0.875rem',
    minWidth: size === 'small' ? 200 : size === 'large' ? 400 : 300,
    maxWidth: maxWidth,
    zIndex: 1400, // Higher than modal backdrop
  }),
  portalContainer: ({ position }) => ({
    position: 'fixed',
    zIndex: 1400,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'auto',
    },
    ...(position === 'top-right' && {
      top: 60,
      right: 16,
    }),
    ...(position === 'top-left' && {
      top: 60,
      left: 16,
    }),
    ...(position === 'bottom-right' && {
      bottom: 16,
      right: 16,
    }),
    ...(position === 'bottom-left' && {
      bottom: 16,
      left: 16,
    }),
  }),
  filledInfo: {
    color: '#3279ae',
    border: '1px solid #3279ae',
    backgroundColor: '#d7eafd',
  },
  filledSuccess: {
    color: '#24A070',
    border: '1px solid #24A070',
    backgroundColor: '#DEF5EC',
  },
  filledWarning: {
    color: '#F47A00',
    border: '1px solid #F47A00',
    backgroundColor: '#FEF1E5',
  },
  filledError: {
    color: '#CC2538',
    border: '1px solid #CC2538',
    backgroundColor: '#f9e3e6',
  },
});

export function showToast(props) {
  openSnackbarFn(props);
}
